type SlateNode = {
  type: string;
  children: SlateNode[];
  text?: string;
  align?: "left" | "right" | "center" | "justify";
  url?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
};

export const slateToHtml = (slateContent: SlateNode[]): string => {
  if (!slateContent || !slateContent.length) return "";
  return (
    slateContent
      ?.map((node) => {
        // Handle different types of nodes based on their structure
        if (node.type === "paragraph") {
          return `<p style="text-align:${getTextAlign(node)}">${node.children
            ?.map((child) => processText(child))
            .join("")}</p>`;
        }

        if (node.type === "bulleted-list") {
          return `<ul>${node.children
            ?.map((child) => `<li>${processListItem(child)}</li>`)
            .join("")}</ul>`;
        }

        if (node.type === "numbered-list") {
          return `<ol>${node.children
            ?.map((child) => `<li>${processListItem(child)}</li>`)
            .join("")}</ol>`;
        }

        if (node.type === "list-item") {
          // If it's a list item, process the children and return as an li
          return `<li>${processListItem(node)}</li>`;
        }

        if (node.type === "quote") {
          return `<blockquote>${node.children
            ?.map((child) => child.text)
            .join("")}</blockquote>`;
        }

        if (node.type === "link") {
          return `<a href="${
            node.url
          }" target="_blank" rel="noopener noreferrer">${node.children
            ?.map((child) => processText(child))
            .join("")}</a>`;
        }

        if (node.type === "bold") {
          return `<strong>${node.children
            ?.map((child) => child.text)
            .join("")}</strong>`;
        }

        if (node.type === "italic") {
          return `<em>${node.children
            ?.map((child) => child.text)
            .join("")}</em>`;
        }

        if (node.type === "underline") {
          return `<u>${node.children?.map((child) => child.text).join("")}</u>`;
        }

        if (node.type === "strikethrough") {
          return `<del>${node.children
            ?.map((child) => child.text)
            .join("")}</del>`;
        }

        return node.children?.map((child) => child.text).join("") || ""; // Default text handling
      })
      .join("") || ""
  ); // Ensure a string is returned
};

// Helper function to process a list item, including handling nested lists
const processListItem = (node: SlateNode): string => {
  return (
    node.children
      ?.map((child) => {
        // If the child is a list, we need to recursively process it
        if (child.type === "bulleted-list" || child.type === "numbered-list") {
          return slateToHtml(child.children); // Recursively process nested lists
        }
        return processText(child); // Process inline text or other elements
      })
      .join("") || ""
  );
};

// Helper function to get text alignment
const getTextAlign = (node: SlateNode): string => {
  if (node.align === "left") return "left";
  if (node.align === "right") return "right";
  if (node.align === "center") return "center";
  if (node.align === "justify") return "justify";
  return "left";
};

// Helper function to process the text content (handling inline formatting)
const processText = (child: SlateNode): string => {
  let processedText = child.text || "";
  if (child.type === "link" && Array.isArray(child.children)) {
    const innerHtml = child.children.map(processText).join("");
    return `<a href="${child.url}" target="_blank" rel="noopener noreferrer">${innerHtml}</a>`;
  }


  if (child.bold) processedText = `<strong>${processedText}</strong>`;
  if (child.italic) processedText = `<em>${processedText}</em>`;
  if (child.underline) processedText = `<u>${processedText}</u>`;
  if (child.strikethrough) processedText = `<del>${processedText}</del>`;

  return processedText;
};

